import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useLocalizeMessage, ModalWindow, Text, Select, Badge, RadioGroup, RadioGroupOption, Input, TextArea, Spinner, useToastNotification, } from 'libs.nucleus';
import { performAtomicVersionUpgrade } from '../../../utils/atomic_version_upgrade.utils';
import { useCharts } from '../../hooks';
import { getStatusBadgeVariant, getStatusLabel } from '../../utils';
// Helper function to get available status options based on current status
const getAvailableStatusOptions = (currentStatus, translate) => {
    if (currentStatus === 'DRAFT') {
        return [{ label: translate('Ready'), value: 'PUBLISHED' }];
    }
    if (currentStatus === 'PUBLISHED') {
        return [{ label: translate('In progress'), value: 'DRAFT' }];
    }
    return [];
};
export const ChangeStatusModal = ({ chart, onClose, onStatusChange }) => {
    const translate = useLocalizeMessage();
    const { addNotification } = useToastNotification();
    const { transitionStatus, cloneChart, archiveChart, checkChartNameUnique, fetchDatasets, isLoading: isApiLoading, } = useCharts();
    const availableOptions = getAvailableStatusOptions(chart.status, translate);
    const [newStatus, setNewStatus] = useState(availableOptions.length > 0 ? availableOptions[0] : undefined);
    // Action selection for PUBLISHED -> DRAFT
    const [selectedAction, setSelectedAction] = useState('CREATE_VERSION');
    // Multi-step state
    const [currentStep, setCurrentStep] = useState('status');
    // Clone form fields
    const [cloneTitle, setCloneTitle] = useState('');
    const [cloneDescription, setCloneDescription] = useState('');
    const [cloneDatasetId, setCloneDatasetId] = useState('');
    const [cloneDatasetKey, setCloneDatasetKey] = useState('');
    // Dataset options for the clone form
    const [datasetOptions, setDatasetOptions] = useState([]);
    const [isLoadingDatasets, setIsLoadingDatasets] = useState(false);
    // Clone title validation
    const [cloneTitleError, setCloneTitleError] = useState('');
    const [isValidatingTitle, setIsValidatingTitle] = useState(false);
    // Debounced title validation
    const validateCloneTitle = useDebouncedCallback(async (titleValue) => {
        if (!titleValue.trim()) {
            setCloneTitleError(translate('Title is required'));
            setIsValidatingTitle(false);
            return;
        }
        setIsValidatingTitle(true);
        try {
            const isUnique = await checkChartNameUnique(titleValue);
            if (!isUnique) {
                setCloneTitleError(translate('A chart with this name already exists'));
            }
            else {
                setCloneTitleError('');
            }
        }
        catch {
            // If validation fails, allow user to proceed
            setCloneTitleError('');
        }
        finally {
            setIsValidatingTitle(false);
        }
    }, 500);
    // Trigger validation when clone title changes
    useEffect(() => {
        if (currentStep === 'clone-form' && cloneTitle) {
            validateCloneTitle(cloneTitle);
        }
        else if (currentStep === 'clone-form') {
            setCloneTitleError('');
        }
    }, [cloneTitle, currentStep, validateCloneTitle]);
    // Check if we're transitioning from Ready to In Progress
    const isReadyToInProgress = chart.status === 'PUBLISHED' && newStatus?.value === 'DRAFT';
    // Initialize clone form fields and load datasets when entering clone form
    useEffect(() => {
        if (currentStep === 'clone-form') {
            if (!cloneTitle) {
                setCloneTitle(chart.name);
            }
            // Pre-select the current dataset
            const existingData = chart.data;
            const currentCubeConfigId = chart.cubeConfigId || existingData?.cubeConfigId || '';
            const currentCubeConfigKey = chart.cubeConfigKey || existingData?.cubeConfigKey || '';
            if (!cloneDatasetId && currentCubeConfigId) {
                setCloneDatasetId(currentCubeConfigId);
                setCloneDatasetKey(currentCubeConfigKey);
            }
            // Fetch datasets for the dropdown
            const loadDatasets = async () => {
                setIsLoadingDatasets(true);
                try {
                    const datasets = await fetchDatasets();
                    setDatasetOptions(datasets.map((ds) => ({ label: ds.name, value: ds.id, key: ds.key })));
                }
                catch {
                    // Error handled in hook
                }
                finally {
                    setIsLoadingDatasets(false);
                }
            };
            loadDatasets();
        }
    }, [currentStep]);
    const handleSubmit = async () => {
        if (isApiLoading) {
            return;
        }
        try {
            if (isReadyToInProgress) {
                if (selectedAction === 'CREATE_VERSION') {
                    // SSP-4186: create the new version and archive the original atomically.
                    // A create-succeeds/archive-fails sequence would otherwise leave both
                    // versions active and duplicate the chart in the Library list.
                    const result = await performAtomicVersionUpgrade({
                        previous: chart,
                        createVersion: () => cloneChart(chart, 'CREATE_VERSION', undefined, { silent: true }),
                        archive: (resource) => archiveChart(resource.id, { silent: true }),
                    });
                    if (result.outcome === 'create_failed') {
                        addNotification({
                            title: translate('Version upgrade failed'),
                            subtitle: translate('Could not create the new chart version. No changes were made.'),
                            type: 'error',
                        });
                        return;
                    }
                    if (result.outcome === 'archive_failed') {
                        addNotification({
                            title: translate('Version upgrade failed'),
                            subtitle: translate('Could not archive the previous version. No changes were kept.'),
                            type: 'error',
                        });
                        return;
                    }
                    addNotification({
                        title: translate('New chart version created'),
                        type: 'success',
                    });
                    onStatusChange(result.newResource);
                    onClose();
                }
                else {
                    // Clone asset - show clone form step
                    setCurrentStep('clone-form');
                }
            }
            else {
                // Regular status change
                if (!newStatus) {
                    return;
                }
                const updated = await transitionStatus(chart.id, newStatus.value);
                onStatusChange(updated);
                onClose();
            }
        }
        catch {
            // Error already handled in hook
        }
    };
    const handleCloneSubmit = async () => {
        if (isApiLoading || !cloneTitle.trim() || cloneTitleError || !cloneDatasetId) {
            return;
        }
        // Final uniqueness check before submit
        setIsValidatingTitle(true);
        try {
            const isUnique = await checkChartNameUnique(cloneTitle.trim());
            if (!isUnique) {
                setCloneTitleError(translate('A chart with this name already exists'));
                setIsValidatingTitle(false);
                return;
            }
        }
        catch {
            // Allow to proceed if check fails
        }
        finally {
            setIsValidatingTitle(false);
        }
        try {
            // Create clone - original stays as Ready
            const newChart = await cloneChart(chart, 'CLONE', {
                name: cloneTitle.trim(),
                description: cloneDescription.trim(),
                cubeConfigId: cloneDatasetId,
                cubeConfigKey: cloneDatasetKey,
            });
            onStatusChange(newChart);
            onClose();
        }
        catch {
            // Error already handled in hook
        }
    };
    const handleBack = () => {
        setCurrentStep('status');
    };
    // Determine button label
    const getSubmitButtonLabel = () => {
        if (currentStep === 'clone-form') {
            return translate('Clone');
        }
        if (isReadyToInProgress && selectedAction === 'CLONE') {
            return translate('Clone asset');
        }
        return translate('Change status');
    };
    const isSubmitDisabled = () => {
        if (currentStep === 'clone-form') {
            return isApiLoading || !cloneTitle.trim() || !!cloneTitleError || isValidatingTitle || !cloneDatasetId;
        }
        return isApiLoading || !newStatus;
    };
    const submitButton = {
        label: getSubmitButtonLabel(),
        onClick: currentStep === 'clone-form' ? handleCloneSubmit : handleSubmit,
        disabled: isSubmitDisabled(),
    };
    const footerSecondaryActionButtons = currentStep === 'clone-form'
        ? [
            {
                label: translate('Cancel'),
                onClick: onClose,
                disabled: isApiLoading,
            },
            {
                label: translate('Back'),
                onClick: handleBack,
                disabled: isApiLoading,
            },
        ]
        : [
            {
                label: translate('Cancel'),
                onClick: onClose,
                disabled: isApiLoading,
            },
        ];
    const getModalSubtitle = () => {
        if (currentStep === 'clone-form') {
            return translate('Enter information for cloning.');
        }
        return undefined;
    };
    const handleDatasetChange = (option) => {
        setCloneDatasetId(option.value);
        setCloneDatasetKey(option.key || '');
    };
    return (_jsxs(ModalWindow, { title: translate('Change status'), subtitle: getModalSubtitle(), isOpen: true, closeWindow: onClose, footerPrimaryActionButton: submitButton, footerSecondaryActionButtons: footerSecondaryActionButtons, children: [currentStep === 'status' && (_jsxs("div", { className: 'space-y-6 p-4', children: [_jsxs("div", { className: 'space-y-1', children: [_jsx(Text, { className: 'text-text-secondary', children: translate('In progress asset can be edited.') }), _jsx(Text, { className: 'text-text-secondary', children: translate('Ready asset can be viewed by users without edit access.') })] }), _jsxs("div", { className: 'flex gap-12 items-center', children: [_jsx(Text, { children: translate('Current status') }), _jsx(Badge, { variant: getStatusBadgeVariant(chart.status), label: getStatusLabel(chart.status, translate) })] }), availableOptions.length === 1 ? (_jsxs("div", { className: 'flex gap-12 items-center', children: [_jsx(Text, { children: translate('New status') }), _jsx(Badge, { variant: getStatusBadgeVariant(newStatus?.value), label: getStatusLabel(newStatus?.value, translate) })] })) : (_jsx(Select, { label: translate('New status'), options: availableOptions, value: newStatus, onChange: (value) => setNewStatus(value), width: 'full' })), isReadyToInProgress && (_jsxs("div", { className: 'space-y-4', children: [_jsx(Text, { className: 'font-medium', children: translate('Action') }), _jsx(RadioGroup, { value: selectedAction, onChange: setSelectedAction, children: _jsxs("div", { className: 'space-y-4', children: [_jsx("div", { className: `p-4 border border-solid rounded-lg cursor-pointer ${selectedAction === 'CREATE_VERSION'
                                                ? 'bg-informative-50 border-informative-600 border-[1.5px]'
                                                : 'bg-surface border-neutral-200 hover:bg-neutral-50'}`, onClick: () => setSelectedAction('CREATE_VERSION'), children: _jsx(RadioGroupOption, { option: {
                                                    value: 'CREATE_VERSION',
                                                    label: translate('Create new version'),
                                                    description: translate('Replace the current asset with a new version. Previous versions become archived and made read-only in the library.'),
                                                } }) }), _jsx("div", { className: `p-4 border border-solid rounded-lg cursor-pointer ${selectedAction === 'CLONE'
                                                ? 'bg-informative-50 border-informative-600 border-[1.5px]'
                                                : 'bg-surface border-neutral-200 hover:bg-neutral-50'}`, onClick: () => setSelectedAction('CLONE'), children: _jsx(RadioGroupOption, { option: {
                                                    value: 'CLONE',
                                                    label: translate('Clone asset'),
                                                    description: translate('Cloning an asset allows the existing one to remain available in the library while you create a new one. The status of the existing asset will not change.'),
                                                } }) })] }) })] }))] })), currentStep === 'clone-form' && (_jsxs("div", { className: 'space-y-6 p-4', children: [_jsxs("div", { className: 'relative', children: [_jsx(Input, { id: 'clone-title', label: translate('Title'), value: cloneTitle, onChange: (e) => {
                                    setCloneTitle(e.target.value);
                                    setCloneTitleError('');
                                }, width: 'full', required: true, hasError: !!cloneTitleError, errorMessage: cloneTitleError, autoComplete: 'off' }), isValidatingTitle && (_jsx("div", { className: 'absolute end-0 top-0', children: _jsx(Spinner, { size: 'sm' }) }))] }), _jsx(TextArea, { id: 'clone-description', label: translate('Description'), value: cloneDescription, onChange: (e) => setCloneDescription(e.target.value), width: 'full' }), _jsx(Select, { label: translate('Dataset'), required: true, options: datasetOptions, value: datasetOptions.find((o) => o.value === cloneDatasetId), onChange: handleDatasetChange, width: 'full', disabled: isLoadingDatasets })] }))] }));
};
